import {
  PropsWithChildren,
  ReactElement,
  Ref,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

export type FormErrors<T extends Record<string, unknown>> = Partial<
  Record<keyof T, string>
>;

export type FieldState<T> = {
  value: T | undefined;
  error: string | undefined;
  setValue: (value: T) => void;
  setError: (error: string | undefined) => void;
};

export type FormFieldsContextState<
  T extends Record<string, unknown> = Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
> = {
  config: C;
  setConfig: (config: C) => void;

  values: T;
  errors: FormErrors<T>;

  setFieldValue: <K extends keyof T>(field: K, value: T[K]) => void;
  setFieldError: <K extends keyof T>(field: K, error: string) => void;
  clearFieldError: <K extends keyof T>(field: K) => void;
  setValues: (values: Partial<T>) => void;
  setErrors: (errors: FormErrors<T>) => void;

  isValid: boolean;
  isComplete: boolean;
  isDirty: boolean;

  showErrors: boolean;
  setShowErrors: (show: boolean) => void;

  setFieldOptions: <K extends keyof T>(
    field: K,
    options: FormFieldOptions | null,
  ) => void;

  resetForm: () => void;
};

const FormFieldsContext = createContext<FormFieldsContextState | undefined>(
  undefined,
);

export type FormFieldsHandle<
  T extends Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
> = FormFieldsContextState<T, C>;

export type FormFieldsProviderProps<
  T extends Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
> = PropsWithChildren<{
  config?: C;
  initialValues?: Partial<T>;
}>;

function FormFieldsProviderInner<
  T extends Record<string, unknown> = Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
>(
  {
    children,
    config: formConfig = {} as C,
    initialValues: initialValuesProp = {},
  }: FormFieldsProviderProps<T, C>,
  ref: Ref<FormFieldsHandle<T, C>>,
) {
  // Callers seed a subset of the form; the rest fills in lazily via
  // `useFormField`, so we treat the partial seed as the initial `T`.
  const initialValues = initialValuesProp as T;

  const [config, setConfig] = useState<C>(formConfig);

  const [values, setValuesState] = useState<T>(initialValues);
  const [errors, setErrorsState] = useState<FormErrors<T>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [fieldOptionsMap, setFieldOptionsMap] = useState<
    Partial<Record<keyof T, FormFieldOptions>>
  >({});

  const setFieldValue = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      setValuesState(prev => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const setFieldError = useCallback(
    <K extends keyof T>(field: K, error: string) => {
      setErrorsState(prev => ({
        ...prev,
        [field]: error,
      }));
    },
    [],
  );

  const clearFieldError = useCallback(<K extends keyof T>(field: K) => {
    setErrorsState(prev => {
      const { [field]: _, ...rest } = prev;
      return rest as FormErrors<T>;
    });
  }, []);

  const setValues = useCallback((newValues: Partial<T>) => {
    setValuesState(prev => ({
      ...prev,
      ...newValues,
    }));
  }, []);

  const setErrors = useCallback((newErrors: FormErrors<T>) => {
    setErrorsState(newErrors);
  }, []);

  const setFieldOptions = useCallback(
    <K extends keyof T>(field: K, options: FormFieldOptions | null) => {
      setFieldOptionsMap(prev => {
        if (options === null) {
          const { [field]: _, ...rest } = prev;
          return rest as Partial<Record<keyof T, FormFieldOptions>>;
        }
        return { ...prev, [field]: options };
      });
    },
    [],
  );

  const isValid = useMemo(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  const isComplete = useMemo(() => {
    const requiredFields = Object.entries(fieldOptionsMap)
      .filter(([, opts]) => opts?.isRequired)
      .map(([field]) => field);

    if (requiredFields.length === 0) return true;

    return requiredFields.every(field => {
      const value = values[field as keyof T];
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;

      return true;
    });
  }, [fieldOptionsMap, values]);

  const isDirty = useMemo(() => {
    return Object.keys(values).some(
      key => values[key as keyof T] !== initialValues[key as keyof T],
    );
  }, [values, initialValues]);

  const resetForm = useCallback(() => {
    setValuesState(initialValues);
    setErrorsState({});
    setShowErrors(false);
  }, [initialValues]);

  const contextValue = useMemo(
    () =>
      ({
        config,
        setConfig,
        values,
        errors,
        setFieldValue,
        setFieldError,
        clearFieldError,
        setValues,
        setErrors,
        isValid,
        isComplete,
        isDirty,
        showErrors,
        setShowErrors,
        setFieldOptions,
        resetForm,
      }) satisfies FormFieldsContextState<T, C> as FormFieldsContextState,
    [
      config,
      setConfig,
      values,
      errors,
      setFieldValue,
      setFieldError,
      clearFieldError,
      setValues,
      setErrors,
      isValid,
      isComplete,
      isDirty,
      showErrors,
      setFieldOptions,
      resetForm,
    ],
  );

  useImperativeHandle(ref, () => contextValue as FormFieldsHandle<T, C>, [
    contextValue,
  ]);

  return (
    <FormFieldsContext.Provider value={contextValue}>
      {children}
    </FormFieldsContext.Provider>
  );
}

export const FormFieldsProvider = forwardRef(FormFieldsProviderInner) as <
  T extends Record<string, unknown> = Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
>(
  props: FormFieldsProviderProps<T, C> & { ref?: Ref<FormFieldsHandle<T, C>> },
) => ReactElement;

export const FormFieldsConsumer = FormFieldsContext.Consumer;

export function useFormFieldsContext<
  T extends Record<string, unknown> = Record<string, unknown>,
  C extends Record<string, unknown> = Record<string, unknown>,
>(): FormFieldsContextState<T, C> {
  const context = useContext(FormFieldsContext);
  if (!context) {
    throw new Error(
      'useFormFieldsContext must be used within a FormFieldsProvider',
    );
  }
  return context as FormFieldsContextState<T, C>;
}

export function useFormFieldsConfig<
  C extends Record<string, unknown> = Record<string, unknown>,
>(): C {
  const { config } = useFormFieldsContext<Record<string, unknown>, C>();
  return config;
}

export type FormFieldOptions<V = unknown> = {
  isRequired?: boolean;
  validate?: (value: V | undefined) => string | undefined;
};

export function useFormField<V>(
  field: string,
  initialValue?: V,
  options?: FormFieldOptions<V>,
): FieldState<V>;
export function useFormField<
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  field: K,
  initialValue?: T[K],
  options?: FormFieldOptions<T[K]>,
): FieldState<T[K]>;
export function useFormField<
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  field: K | string,
  initialValue?: T[K] | unknown,
  options?: FormFieldOptions<T[K]>,
): FieldState<T[K]> | FieldState<unknown> {
  const {
    values,
    errors,
    setFieldValue,
    setFieldError,
    clearFieldError,
    setFieldOptions: setFieldOpts,
  } = useFormFieldsContext<T>();

  const setValue = useCallback(
    (value: T[K] | unknown) => setFieldValue(field as K, value as T[K]),
    [field, setFieldValue],
  );

  const setError = useCallback(
    (error: string | undefined) => {
      if (error) {
        setFieldError(field as K, error);
      } else {
        clearFieldError(field as K);
      }
    },
    [field, setFieldError, clearFieldError],
  );

  useEffect(() => {
    if (!(field in values) && initialValue !== undefined) {
      setFieldValue(field as K, initialValue as T[K]);
    }
  }, [values, setFieldValue, field, initialValue]);

  useEffect(() => {
    if (!options) return undefined;
    setFieldOpts(field as K, { isRequired: options.isRequired });
    return () => setFieldOpts(field as K, null);
  }, [field, options?.isRequired, setFieldOpts]);

  const validationError = options?.validate?.(values[field] as T[K]);

  useEffect(() => {
    if (validationError) {
      setFieldError(field as K, validationError);
    } else {
      clearFieldError(field as K);
    }
    return () => clearFieldError(field as K);
  }, [field, validationError, setFieldError, clearFieldError]);

  return {
    value: values[field],
    error: errors[field],
    setValue,
    setError,
  };
}

export type FormFieldProps<V> = {
  field?: string;
  initialValue?: V;
  isRequired?: boolean;
};
